import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import useVisitStore from '../../stores/visitStore';
import VisitActionsPanel from '../visit/VisitActionsPanel';
import { WidgetHost } from '../../widgets';
import ChartSlot from '../../charts/core/ChartSlot';
import useChartType from '../../charts/core/useChartType';
import useTabletDentalChartStore from '../../charts/plugins/dental/tablet/useTabletDentalChartStore';
import TabletDentalChartHost from '../../charts/plugins/dental/tablet/TabletDentalChartHost';
import PatientHistory from '../visit/PatientHistory';
import AddPrescription from '../visit/AddPrescription';
import DigitalPrescriptionForm from '../visit/DigitalPrescriptionForm';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import { fetchVisitMetadata } from '../../services/api/visitMetadataApi';
import { META_TYPES } from '../../constants/visitMetadata';
import { PhoneButton } from '../common/PhoneButton';
import { formatToken, isAppointmentVisit } from '../../utils/formatToken';
import { isDigitalRxEntry } from '../../utils/rxEntry';
import { STAFF_ROLES } from '../../constants/staff';
import syncEngine from '../../services/SyncEngine';
import PatientInfoEditable from '../common/PatientInfoEditable/PatientInfoEditable';
import PracticeTourNudge from '../common/PracticeTourNudge';
import useOwnDoctorSpecialization from '../../hooks/useOwnDoctorSpecialization';
import WalkinNotificationCard from '../procedures/WalkinNotificationCard';
import HandoffBadge from '../procedures/HandoffBadge';
import CoworkerSlotBadge from '../visit/CoworkerSlotBadge';
import {
  patientName, StatPill,
  DoctorAvailabilityToggle, UnifiedActionBar,
  CancelConfirmDialog, FollowUpDialog,
} from './WorkflowShared';
import SendVisitDialog from '../visit/SendVisitDialog';
import IncomingReferralBanner from '../visit/IncomingReferralBanner';
import VisitJourney from '../visit/VisitJourney';
import { usePermission } from '../../context/PermissionContext';
import useVisitCompletionGuard from '../../hooks/useVisitCompletionGuard';

// FE-19 — the doctor could not SEE either of these. "View Full Queue →" was a
// dashed, transparent, 10px light-grey control (invisible without hover, and
// dashed reads as a drop target, which it is not), and the Up Next list was
// pale-on-pale: the token `#d1d5db` on white is barely there. Both now follow
// the frontend button-visibility / transparency rules — solid surfaces in BOTH
// modes, mid-tone borders, primary ink for the patient's name. Defined ONCE and
// rendered by both the in-progress and the idle layout, which are the two
// places that carried the same copy-pasted style block.
function FullQueueButton({ isDark, onClick }) {
  const [hover, setHover] = useState(false);
  const teal = isDark ? '#2dd4bf' : '#0d9488';
  return (
    <button
      onClick={onClick}
      data-testid="view-full-queue"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: 'pointer',
        width: '100%',
        padding: '0.5rem',
        borderRadius: '0.5rem',
        border: `1px solid ${hover ? teal : (isDark ? 'rgba(255,255,255,0.24)' : '#94a3b8')}`,
        background: isDark ? '#1e293b' : '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: hover ? teal : (isDark ? '#f1f5f9' : '#1e293b'),
        textAlign: 'center',
        transition: 'border-color 0.15s, color 0.15s',
      }}
    >
      View Full Queue →
    </button>
  );
}

/** One ink set for the Up Next card — never opacity to demote a line. */
const upNextTone = (isDark) => ({
  surface: {
    background: isDark ? '#1e293b' : '#f8fafc',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1'}`,
    borderRadius: '0.5rem',
    padding: '0.5rem',
  },
  heading: {
    margin: '0 0 0.375rem', fontSize: '0.6875rem', fontWeight: 700,
    color: isDark ? '#94a3b8' : '#475569',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  token: {
    width: '1.75rem', textAlign: 'center', fontSize: '0.6875rem', fontWeight: 700,
    color: isDark ? '#cbd5e1' : '#334155',
  },
  name: {
    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a',
  },
  more: {
    margin: '0.25rem 0 0', fontSize: '0.6875rem', fontWeight: 600,
    color: isDark ? '#94a3b8' : '#475569',
  },
});

// DoctorView — Full inline consultation workflow for doctors
export default function DoctorView({ inProgress, queue, dailyVisits, isDark, loading, onStart, onNavigate, updateVisit, role, skipVisit, memberId, doctorAvailability, updateDoctorAvailability }) {
  const patchVisitFields = useVisitStore((s) => s.patchVisitFields);
  const [activeTab, setActiveTab] = useState(null);     // unified tab: 'vitals' | 'symptoms' | 'notes' | 'prescription' | 'history' | null
  const [historyPatient, setHistoryPatient] = useState(null);
  const [justCompleted, setJustCompleted] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [followUpDialog, setFollowUpDialog] = useState(null); // { visitId, name }
  const [pendingComplete, setPendingComplete] = useState(false); // true = prescription panel open for review before complete
  const [skipFlash, setSkipFlash] = useState(null); // { name, nextName, nextQueue }
  const [isMaximized, setIsMaximized] = useState(false);
  // Phase 3.1.3 — Send Visit dialog state
  const [sendVisitDialog, setSendVisitDialog] = useState(null);
  const canReferVisit = usePermission('REFER_VISIT');
  const canCompleteVisit = usePermission('COMPLETE_VISIT');
  const canManageQueue = usePermission('MANAGE_QUEUE');
  const workflowRef = useRef(null);
  const prescriptionRef = useRef(null);
  const prevQueueIdsRef = useRef(new Set());

  // Coarse-pointer (tablet/phone) dentist chairside handoff. When the
  // doctor taps the ProcedureWidget's `Q-N · count` chip on touch, we
  // open the dental tooth chart full-bleed instead of expanding the
  // rail. Desktop dentists and non-dentists fall through to today's
  // rail-expand behaviour — this handler returns falsy in those cases
  // so the widget defaults to `setSize('focused')`.
  const isDentist = useChartType() === 'dental';
  const openTabletDentalChart = useTabletDentalChartStore((s) => s.open);
  // Bridge for ProcedureWidget — when the tablet dental chart is paired
  // alongside the procedure rail, the widget must lock at 'focused' and
  // square its left corners. The widget is chart-agnostic, so we
  // subscribe here (the integration point that already knows about both
  // surfaces) and pass it down through WidgetHost.
  const isTabletDentalChartOpen = useTabletDentalChartStore((s) => s.isOpen);
  const handleProcedureCoarseShrunkTap = useCallback(({ visit, viewer }) => {
    if (!isDentist || !visit) return false;
    openTabletDentalChart(visit, viewer);
    return true;
  }, [isDentist, openTabletDentalChart]);

  // Auto-scroll to prescription panel when pendingComplete triggers
  useEffect(() => {
    if (pendingComplete && activeTab === 'prescription' && prescriptionRef.current) {
      prescriptionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [pendingComplete, activeTab]);

  /* ── Maximize: lock body scroll + Escape key ── */
  useEffect(() => {
    if (!isMaximized) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setIsMaximized(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isMaximized]);

  // ── Availability config ──────────────────────────────────────────
  const AVAILABILITY_OPTIONS = [
    { key: 'available',     label: 'Available',       icon: '🟢', color: '#22c55e' },
    { key: 'on_hold',       label: 'On Hold',         icon: '🟡', color: '#f59e0b' },
    { key: 'closed_today',  label: 'Closed for Today', icon: '🔴', color: '#ef4444' },
  ];
  const currentAvail = AVAILABILITY_OPTIONS.find((o) => o.key === doctorAvailability) || AVAILABILITY_OPTIONS[0];

  // Workflow tracking — patterns drive tab ordering
  const {
    logAction: logWorkflow,
    patterns,
    fetchPatterns,
  } = useWorkflowStore();

  // Phase 3.1.3 — filter out visits the doctor has already SENT
  // somewhere (refer / send-for-action). On the park path the
  // backend keeps `doctor_member_id == this doctor` until the
  // receptionist forwards, but UX-wise the doctor is "done with"
  // those visits — they should drop out of the active slot so the
  // workflow advances to the next patient. We hide any in-progress
  // visit whose action-pending overlay is held by someone other
  // than the doctor themselves (covers REFERRAL_TO_DOCTOR,
  // COLLECT_PAYMENT, CLOSE_AFTER_PAYMENT, ASSIGN_BACK_TO_ME).
  const visibleInProgress = useMemo(
    () => (inProgress || []).filter((v) => {
      const holder = v.action_pending_with_member_id;
      if (!holder) return true;          // no overlay → show
      if (holder === memberId) return true; // I'm the holder → show
      return false;                      // sent elsewhere → hide
    }),
    [inProgress, memberId],
  );

  // Current active visit (first remaining IN_PROGRESS).
  const currentVisit = visibleInProgress.length > 0 ? visibleInProgress[0] : null;

  // Auto-open prescription tab when consultation starts
  const prevVisitIdRef = useRef(null);
  useEffect(() => {
    const vid = currentVisit?.visit_id;
    if (vid && vid !== prevVisitIdRef.current) {
      setActiveTab('prescription');
    }
    prevVisitIdRef.current = vid || null;
  }, [currentVisit?.visit_id]);

  // ── Skip flash detection ─────────────────────────────────────────
  // When a visit disappears from queue and appears as SKIPPED in dailyVisits, flash a notification
  useEffect(() => {
    const currentIds = new Set(queue.map((v) => v.visit_id));
    const prevIds = prevQueueIdsRef.current;

    if (prevIds.size > 0) {
      // Find visits that were in queue but now aren't
      for (const id of prevIds) {
        if (!currentIds.has(id)) {
          const skippedVisit = (dailyVisits || []).find((v) => v.visit_id === id && v.status === 'SKIPPED');
          if (skippedVisit) {
            const nextPatient = queue[0];
            setSkipFlash({
              name: patientName(skippedVisit),
              nextName: nextPatient ? patientName(nextPatient) : null,
              nextQueue: nextPatient?.queue_number,
            });
            setTimeout(() => setSkipFlash(null), 4000);
            break; // only flash for the first detected skip
          }
        }
      }
    }
    prevQueueIdsRef.current = currentIds;
  }, [queue, dailyVisits]);

  // Req 6 — post-complete UI reset + workflow log. Runs after the visit is
  // actually marked COMPLETED — on the no-open-procedures path AND on every
  // resolution of the open-procedures prompt (via the shared guard).
  const handleAfterComplete = useCallback((visitId) => {
    setPendingComplete(false);
    setActiveTab(null);
    setJustCompleted(visitId);
    logWorkflow(visitId, WORKFLOW_ACTIONS.MARK_COMPLETED);
    setTimeout(() => setJustCompleted(null), 1500);
  }, [logWorkflow]);

  // Req 6 — shared completion guard: prompts when the visit still has open
  // procedures (incl. delegated), same behaviour as the Manage Visit screen.
  const { requestComplete, completionPromptNode } = useVisitCompletionGuard({
    updateVisit,
    viewer: { memberId, role },
    onCompleted: handleAfterComplete,
  });
  const findVisitById = useCallback((visitId) => (
    [inProgress, ...(Array.isArray(queue) ? queue : []), ...(Array.isArray(dailyVisits) ? dailyVisits : [])]
      .find((v) => v?.visit_id === visitId) || { visit_id: visitId }
  ), [inProgress, queue, dailyVisits]);

  // Complete consultation — surface the prescription for review first (unless one
  // already exists / we're already reviewing), then run the open-procedures guard
  // at the actual-complete moment.
  const handleComplete = useCallback(async (visitId) => {
    if (pendingComplete && activeTab === 'prescription') {
      await requestComplete(findVisitById(visitId));
      return;
    }
    try {
      const result = await syncEngine.fetchMetadata(
        visitId,
        async (vid) => fetchVisitMetadata(vid),
        { source: 'handleComplete' }
      );
      const entries = result?.data?.entries || [];
      const hasPrescription = entries.some(e => e.meta_type === META_TYPES.PRESCRIPTION);
      if (hasPrescription) {
        await requestComplete(findVisitById(visitId));
        return;
      }
    } catch { /* fallback to review flow */ }
    // No prescription found — open prescription panel for review
    setPendingComplete(true);
    setActiveTab('prescription');
  }, [requestComplete, findVisitById, pendingComplete, activeTab]);

  // Follow-up dialog
  const handleFollowUp = useCallback((visitId) => {
    const visit = (inProgress || []).concat(queue || []).find((v) => v.visit_id === visitId);
    const name = visit ? patientName(visit) : 'this patient';
    setFollowUpDialog({ visitId, name });
  }, [inProgress, queue]);

  const confirmFollowUp = useCallback(async (meta) => {
    if (!followUpDialog) return;
    const { visitId } = followUpDialog;
    setFollowUpDialog(null);
    await updateVisit(visitId, {
      report_needed: meta.reportNeeded,
      follow_up_required: meta.followUpRequired,
      follow_up_date: meta.followUpDate || null,
    });
  }, [followUpDialog, updateVisit]);

  const confirmCancelVisit = useCallback(async (comment) => {
    if (!cancelConfirm) return;
    const { visitId } = cancelConfirm;
    setCancelConfirm(null);
    setActiveTab(null);
    await updateVisit(visitId, { status: 'CANCELLED', cancellation_reason: comment, cancelled_by_role: role });
    logWorkflow(visitId, WORKFLOW_ACTIONS.MARK_CANCELLED);
  }, [cancelConfirm, updateVisit, logWorkflow, role]);

  // Toggle tab — click again to collapse
  const handleTabToggle = useCallback((tabKey) => {
    setActiveTab((prev) => (prev === tabKey ? null : tabKey));
    if (tabKey !== 'prescription') setPendingComplete(false);
  }, []);

  // Metadata tracking
  const [detailCounts, setDetailCounts] = useState({});
  const markHasDetails = useCallback((visitId, has) => {
    setDetailCounts((prev) => ({ ...prev, [visitId]: has }));
  }, []);

  // Fetch workflow patterns once (drives tab ordering)
  useEffect(() => {
    if (!patterns) fetchPatterns();
  }, [patterns, fetchPatterns]);

  // ── Completed count ─────────────────────────────────────────────
  const completedToday = useMemo(
    () => (dailyVisits || []).filter((v) => v.status === 'COMPLETED').length,
    [dailyVisits]
  );

  // ── Just-completed flash ────────────────────────────────────────
  if (justCompleted) {
    return (
      <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
        <span style={{ fontSize: '2rem' }}>✅</span>
        <p style={{ margin: '0.5rem 0 0.25rem', fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#4ade80' : '#16a34a' }}>
          Consultation Complete
        </p>
        <p style={{ margin: 0, fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
          {queue.length > 0
            ? 'Loading next patient…'
            : 'All done for now!'}
        </p>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // STATE 1: Active consultation — full inline workflow
  // ══════════════════════════════════════════════════════════════════
  if (currentVisit) {
    // Widget Library — page-level widget host. Always mounts the
    // ProcedureWidget (the half-disc rail) for the active consultation
    // visit, and renders any spawn-on-demand widget (Diagnosis,
    // FollowUp, VisitCancel, TaskResolution) opened via the SDK.
    // Replaces the legacy ProcedureHalfDiscWidget direct mount.
    //
    // ChartSlot is mounted alongside as the architectural anchor for
    // specialty chart plugins (dental today; ortho/neuro/ENT later).
    // It is intentionally inert in MVP — the dental chart still mounts
    // via OdontogramSection / ToothFindingsSummary for backward-
    // compatible event-driven open/close. A follow-up PR will route
    // those events through ChartSlot and centralise the mount.
    //
    // Phase 2 Bug A: WidgetHost + ChartSlot are rendered as SIBLINGS of
    // the maximized `workflowRef` div (NOT children). Otherwise the
    // maximized div's `position: fixed; inset: 0; zIndex: 1100`
    // establishes a stacking context that occludes the right-edge rail
    // — the dentist couldn't reach the procedure widget while
    // maximized. Siblings keep the rail's own `position: fixed`
    // stacking independent.
    return (
      <>
        <div ref={workflowRef} style={{
          display: 'flex', flexDirection: 'column', gap: '0.625rem',
          ...(isMaximized && {
            position: 'fixed', inset: 0, zIndex: 1100,
            background: isDark ? '#111827' : '#ffffff',
            overflowY: 'auto',
            padding: '1rem',
          }),
        }}>
        {/* Phase 3.2/3.3 — visit journey strip with "where is it now"
            headline + actor-grouped narrative. Self-hides for fresh
            visits with no events. */}
        <VisitJourney
          visitId={currentVisit?.visit_id}
          viewerMemberId={memberId}
          viewerRole="DOCTOR"
          compact
        />
        {/* Phase 3.1.5 — referral context for the receiving doctor.
            Renders nothing for non-referral visits (component nullable
            on missing referral_history hop targeting the viewer). */}
        <IncomingReferralBanner
          visit={currentVisit}
          viewerId={memberId}
        />
        <WalkinNotificationCard memberId={memberId} isDark={isDark} />
        {/* ── Status strip ──────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Pulsing dot */}
            <div style={{ position: 'relative', width: '0.5rem', height: '0.5rem' }}>
              <div style={{
                width: '0.5rem', height: '0.5rem', borderRadius: '50%', background: '#8b5cf6',
                animation: 'pulse 2s 3',
              }} />
            </div>
            <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#8b5cf6' }}>
              In Consultation
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <StatPill label="Completed" value={completedToday} color="#4ade80" isDark={isDark} />
            {queue.length > 0 && (
              <StatPill label="Waiting" value={queue.length} color="#3b82f6" isDark={isDark} />
            )}
            {/* Expand / Minimize Smart Workflow */}
            <button
              onClick={() => setIsMaximized(v => !v)}
              title={isMaximized ? 'Minimize (Esc)' : 'Expand to fullscreen'}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.08)',
                border: `1px solid ${isDark ? 'rgba(13,148,136,0.25)' : 'rgba(13,148,136,0.15)'}`,
                borderRadius: '0.375rem',
                color: isDark ? '#2dd4bf' : '#0f766e',
                cursor: 'pointer',
                padding: '0.2rem 0.45rem',
                fontSize: '0.5625rem',
                fontWeight: 600,
                fontFamily: 'system-ui, sans-serif',
                lineHeight: 1,
                transition: 'all 0.15s ease',
                gap: '0.2rem',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = isDark ? 'rgba(13,148,136,0.2)' : 'rgba(13,148,136,0.15)';
                e.currentTarget.style.transform = 'scale(1.04)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.08)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {isMaximized ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 14 4 10 0 10" /><polyline points="12 2 12 6 16 6" /><line x1="14" y1="0" x2="9" y2="5" /><line x1="2" y1="16" x2="7" y2="11" />
                  </svg>
                  Minimize
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="14 2 14 6 10 6" /><polyline points="2 14 2 10 6 10" /><line x1="14" y1="2" x2="9" y2="7" /><line x1="2" y1="14" x2="7" y2="9" />
                  </svg>
                  Expand
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Patient card ──────────────────────────────────────── */}
        <div style={{
          padding: '0.75rem',
          borderRadius: '0.75rem',
          border: `1px solid ${isDark ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.15)'}`,
          background: isDark
            ? 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(124,58,237,0.04))'
            : 'linear-gradient(135deg, rgba(139,92,246,0.05), rgba(124,58,237,0.02))',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.5rem' }}>
            {/* Queue badge */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: '1.75rem', height: '1.75rem', borderRadius: '0.5rem',
              background: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)',
              fontSize: '0.75rem', fontWeight: 700, color: '#8b5cf6',
            }}>
              {formatToken(currentVisit.queue_number, currentVisit.appointment_number, { hash: false }) || '·'}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <PatientInfoEditable
                  visit={currentVisit}
                  isDark={isDark}
                  editable
                  onUpdate={(fields) => patchVisitFields(currentVisit.visit_id, fields)}
                />
                <PhoneButton phoneNumber={currentVisit.patient_mobile} isDark={isDark} size="sm" />
                {isAppointmentVisit(currentVisit) && <span style={{ fontSize: '0.5625rem', padding: '0.0625rem 0.25rem', borderRadius: '0.25rem', background: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)', color: '#a855f7', fontWeight: 600, flexShrink: 0 }}>📅 Appt</span>}
                <HandoffBadge visit={currentVisit} viewerMemberId={memberId} isDark={isDark} />
                <CoworkerSlotBadge visit={currentVisit} />
              </p>
              <p style={{ margin: '0.0625rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                {currentVisit.visit_type === 'APPOINTMENT' && '📅 Appointment · '}
                {currentVisit.visit_type === 'EMERGENCY' && '🔴 Emergency · '}
                {currentVisit.visit_type === 'FOLLOW_UP' && '🔄 Follow-up · '}
                {currentVisit.visit_type === 'REPORT_CHECKUP' && '📄 Report Checkup · '}
                {currentVisit.visit_time || ''}
              </p>
            </div>
          </div>

          {/* ── Unified Action Bar — powered by workflow patterns ── */}
          <UnifiedActionBar
            isDark={isDark}
            activeTab={activeTab}
            onTabToggle={handleTabToggle}
            excludeTabs={['vitals', 'symptoms', 'report']}
            onComplete={() => handleComplete(currentVisit.visit_id)}
            onFollowUp={() => handleFollowUp(currentVisit.visit_id)}
            onSkip={() => skipVisit?.(currentVisit.visit_id, role)}
            onHistory={() => {
              logWorkflow(currentVisit.visit_id, WORKFLOW_ACTIONS.VIEW_PATIENT_HISTORY);
              setHistoryPatient({
                patient_id: currentVisit.patient_id,
                patient_name: patientName(currentVisit),
                mobile: currentVisit.patient_mobile,
              });
            }}
            loading={loading}
            patterns={patterns}
            detailCount={detailCounts[currentVisit.visit_id]}
            followUpAlreadySet={!!currentVisit.follow_up_required}
            followUpDate={currentVisit.follow_up_date}
            pendingComplete={pendingComplete}
          />

          {/* Phase 3.1.3 — Send Visit (refer to another doctor) on the
              doctor's own queue. Same broad gate as ManageVisitPage so
              cached/stale sessions don't hide it; backend re-enforces
              the actual REFER_VISIT permission on the API call. */}
          {(canReferVisit || canCompleteVisit || canManageQueue)
            && currentVisit.status === 'IN_PROGRESS' && (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setSendVisitDialog(currentVisit)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '0.375rem 0.75rem', borderRadius: '0.5rem',
                  fontSize: '0.75rem', fontWeight: 600, border: 'none',
                  cursor: 'pointer',
                  background: '#d97706',
                  color: '#ffffff',
                }}
              >
                ➤ Send Visit
              </button>
            </div>
          )}

          {/* ── Panel: opens below the tab bar, closes when tab is toggled off ── */}
          {activeTab && activeTab !== 'history' && activeTab !== 'prescription' && (
            <VisitActionsPanel
              visit={currentVisit}
              userRole={role}
              initialTab={activeTab}
              hideActionBar
              onClose={() => setActiveTab(null)}
              onMetadataLoaded={(count) => markHasDetails(currentVisit.visit_id, count > 0)}
            />
          )}

          {activeTab === 'prescription' && (
            <div
              ref={prescriptionRef}
              style={pendingComplete ? {
                borderRadius: '0.75rem',
                boxShadow: isDark
                  ? '0 0 0 2px rgba(34,197,94,0.4), 0 0 12px rgba(34,197,94,0.15)'
                  : '0 0 0 2px rgba(34,197,94,0.35), 0 0 12px rgba(34,197,94,0.1)',
                transition: 'box-shadow 0.3s ease',
              } : undefined}
            >
              <DoctorPrescriptionPanel
                key={currentVisit.visit_id}
                visit={currentVisit}
                isDark={isDark}
                onClose={() => { setPendingComplete(false); setActiveTab(null); }}
                pendingComplete={pendingComplete}
                onConfirmComplete={() => handleComplete(currentVisit.visit_id)}
                role={role}
              />
            </div>
          )}
        </div>

        {/* ── Up Next preview ───────────────────────────────────── */}
        {queue.length > 0 && (
          <div data-testid="up-next-card" style={upNextTone(isDark).surface}>
            <p style={upNextTone(isDark).heading}>
              Up Next
            </p>
            {queue.slice(0, 3).map((v) => (
              <div key={v.visit_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
                <span data-testid="up-next-token" style={upNextTone(isDark).token}>
                  {v.queue_number ? formatToken(v.queue_number, v.appointment_number) : '·'}
                </span>
                <span data-testid="up-next-name" style={upNextTone(isDark).name}>
                  {patientName(v)}
                </span>
                {/* Phase 3.4 — referred-in marker so the doctor instantly
                    spots visits routed via Receptionist; original clinic
                    queue token shown for cross-reference. */}
                {v.parent_queue_number != null && (
                  <span
                    title={`Referred in · originally #${v.parent_queue_number}`}
                    style={{
                      fontSize: '0.5625rem', fontWeight: 600,
                      padding: '0.0625rem 0.3125rem',
                      borderRadius: '9999px',
                      border: `1px solid ${isDark ? 'rgba(251,146,60,0.3)' : 'rgba(251,146,60,0.4)'}`,
                      background: isDark ? 'rgba(251,146,60,0.15)' : 'rgba(251,146,60,0.1)',
                      color: isDark ? '#fdba74' : '#c2410c',
                    }}
                  >
                    🟠 #{v.parent_queue_number}
                  </span>
                )}
                <PhoneButton phoneNumber={v.patient_mobile} isDark={isDark} size="sm" />
                <HandoffBadge visit={v} viewerMemberId={memberId} isDark={isDark} />
                <CoworkerSlotBadge visit={v} />
                {v.visit_type === 'EMERGENCY' && <span style={{ fontSize: '0.5625rem' }}>🔴</span>}
              </div>
            ))}
            {queue.length > 3 && (
              <p style={upNextTone(isDark).more}>
                +{queue.length - 3} more
              </p>
            )}
          </div>
        )}

        {/* ── View full queue link ──────────────────────────────── */}
        <FullQueueButton isDark={isDark} onClick={() => onNavigate?.(null)} />

        {/* Patient History Modal — portalled to body */}
        {historyPatient && ReactDOM.createPortal(
          <PatientHistory
            patientId={historyPatient.patient_id}
            patientName={historyPatient.patient_name}
            phoneNumber={historyPatient.mobile}
            onClose={() => setHistoryPatient(null)}
          />,
          document.body
        )}

        {/* Cancel Confirmation Dialog — portalled to body */}
        {cancelConfirm && ReactDOM.createPortal(
          <CancelConfirmDialog
            isDark={isDark}
            name={cancelConfirm.name}
            onConfirm={confirmCancelVisit}
            onCancel={() => setCancelConfirm(null)}
          />,
          document.body
        )}

        {/* Follow Up Dialog — portalled to body */}
        {followUpDialog && ReactDOM.createPortal(
          <FollowUpDialog
            isDark={isDark}
            name={followUpDialog.name}
            onConfirm={confirmFollowUp}
            onCancel={() => setFollowUpDialog(null)}
          />,
          document.body
        )}

        {/* Phase 3.1.3 — Send Visit dialog — portalled to body so the
            modal escapes the doctor-view's stacking context. On submit
            we refetch the visit list so the just-referred visit drops
            out of the doctor's queue (backend already flipped
            doctor_member_id; the local store needs to re-pull). */}
        {sendVisitDialog && ReactDOM.createPortal(
          <SendVisitDialog
            visit={sendVisitDialog}
            viewer={{ memberId, role }}
            onSubmitted={() => {
              setSendVisitDialog(null);
              // Force the active-visits store to refresh so the visit
              // we just referred away drops out of `inProgress`.
              try { useVisitStore.getState().fetchVisits(); } catch { /* noop */ }
            }}
            onCancel={() => setSendVisitDialog(null)}
          />,
          document.body,
        )}
        {/* Req 6 — open-procedures confirmation when completing a visit. */}
        {completionPromptNode}
        </div>
        {/* Widget rail + chart slot at root level — siblings of the
            (potentially-maximized) workflowRef div. Their
            `position: fixed` stacking is independent of the visit
            panel's stacking context, so the procedure rail stays
            reachable whether the panel is normal or maximized. */}
        <WidgetHost
          visit={currentVisit}
          viewer={{ memberId, role }}
          onProcedureCoarseShrunkTap={handleProcedureCoarseShrunkTap}
          procedurePairedFullSize={isTabletDentalChartOpen}
        />
        <ChartSlot
          visit={currentVisit}
          viewer={{ memberId, role }}
        />
        {/* Chairside dental chart for dentists on coarse-pointer devices.
            Subscribes to useTabletDentalChartStore and renders nothing
            until the chip-tap handler above flips it open. */}
        <TabletDentalChartHost />
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // STATE 2: No active consultation — Queue CTA
  // ══════════════════════════════════════════════════════════════════
  if (queue.length > 0) {
    const first = queue[0];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <WalkinNotificationCard memberId={memberId} isDark={isDark} />
        {/* ── Skip flash notification ─────────────────────────────── */}
        {skipFlash && (
          <div style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.625rem',
            border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
            background: isDark
              ? 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,6,0.05))'
              : 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(217,119,6,0.03))',
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#f59e0b' : '#d97706' }}>
              ⏭ {skipFlash.name} was skipped
            </p>
            {skipFlash.nextName && (
              <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                Next: {skipFlash.nextName} {skipFlash.nextQueue ? `(#${skipFlash.nextQueue})` : ''}
              </p>
            )}
          </div>
        )}

        {/* ── Doctor availability toggle ────────────────────────────── */}
        <DoctorAvailabilityToggle
          isDark={isDark}
          currentAvail={currentAvail}
          options={AVAILABILITY_OPTIONS}
          onSelect={(key) => memberId && updateDoctorAvailability?.(memberId, key)}
        />

        {/* Rx Practice Tour nudge — shows for doctors who haven't tried it yet */}
        <PracticeTourNudge isDark={isDark} />

        {/* Status bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: '1.5rem', height: '1.5rem', borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
              fontSize: '0.75rem', fontWeight: 700, color: '#fff', padding: '0 0.375rem',
            }}>
              {queue.length}
            </span>
            <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 500, color: isDark ? '#e5e7eb' : '#374151' }}>
              {queue.length === 1 ? 'patient' : 'patients'} in queue
            </p>
          </div>
          {completedToday > 0 && (
            <StatPill label="Done" value={completedToday} color="#4ade80" isDark={isDark} />
          )}
        </div>

        {/* Start Consultation CTA */}
        <button
          onClick={() => onStart?.(first.visit_id)}
          disabled={loading}
          style={{
            width: '100%',
            cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: '0.875rem',
            padding: '0.875rem 1rem', borderRadius: '0.75rem',
            border: 'none',
            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
            color: '#fff', transition: 'background 0.2s ease, opacity 0.2s ease',
            opacity: loading ? 0.7 : 1, textAlign: 'left',
          }}
        >
          <span style={{ fontSize: '1.25rem' }}>▶</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 600 }}>
              Start Consultation
            </p>
            <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', opacity: 0.85 }}>
              <PatientInfoEditable visit={first} isDark compact editable={false} />
              {first.queue_number ? ` · #${first.queue_number}` : ''}
              {first.visit_type === 'EMERGENCY' ? ' · 🔴 Emergency' : ''}
            </p>
          </div>
          <span style={{ fontSize: '0.75rem', opacity: 0.8, fontWeight: 500, whiteSpace: 'nowrap' }}>
            {loading ? 'Starting…' : 'Next →'}
          </span>
        </button>

        {/* History + Phone + Skip — inline row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* History button */}
          <button
            onClick={() => {
              logWorkflow(first.visit_id, WORKFLOW_ACTIONS.VIEW_PATIENT_HISTORY);
              setHistoryPatient({
                patient_id: first.patient_id,
                patient_name: patientName(first),
                mobile: first.patient_mobile,
              });
            }}
            style={{
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.4375rem 0.75rem', borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.15)'}`,
              background: isDark ? 'rgba(20,184,166,0.08)' : 'rgba(20,184,166,0.04)',
              fontSize: '0.6875rem', fontWeight: 500,
              color: '#14b8a6',
              transition: 'background 0.15s',
            }}
          >
            <span>📋</span>
            <span>History</span>
          </button>

          {/* Phone button */}
          <PhoneButton phoneNumber={first.patient_mobile} isDark={isDark} size="md" />

          {/* Handoff status badge */}
          <HandoffBadge visit={first} viewerMemberId={memberId} isDark={isDark} />
          <CoworkerSlotBadge visit={first} />

          {/* Skip button */}
          <button
            onClick={() => skipVisit?.(first.visit_id, role)}
            style={{
              flex: 1,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
              padding: '0.4375rem 0.75rem', borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.15)'}`,
              background: isDark ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.04)',
              fontSize: '0.6875rem', fontWeight: 500,
              color: isDark ? '#f59e0b' : '#d97706',
              transition: 'background 0.15s',
            }}
          >
            <span>⏭</span>
            <span>Skip</span>
          </button>
        </div>

        {/* Up Next preview */}
        {queue.length > 1 && (
          <div data-testid="up-next-card" style={upNextTone(isDark).surface}>
            <p style={upNextTone(isDark).heading}>
              Up Next
            </p>
            {queue.slice(1, 4).map((v) => (
              <div key={v.visit_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0' }}>
                <span data-testid="up-next-token" style={upNextTone(isDark).token}>
                  {v.queue_number ? `#${v.queue_number}` : '·'}
                </span>
                <span data-testid="up-next-name" style={upNextTone(isDark).name}>
                  {patientName(v)}
                </span>
                <PhoneButton phoneNumber={v.patient_mobile} isDark={isDark} size="sm" />
                {v.visit_type === 'EMERGENCY' && <span style={{ fontSize: '0.625rem' }}>🔴</span>}
              </div>
            ))}
            {queue.length > 4 && (
              <p style={upNextTone(isDark).more}>
                +{queue.length - 4} more
              </p>
            )}
          </div>
        )}

        {/* View full queue */}
        <FullQueueButton isDark={isDark} onClick={() => onNavigate?.(null)} />

        {/* Patient History Modal — portalled to body (idle queue) */}
        {historyPatient && ReactDOM.createPortal(
          <PatientHistory
            patientId={historyPatient.patient_id}
            patientName={historyPatient.patient_name}
            phoneNumber={historyPatient.mobile}
            onClose={() => setHistoryPatient(null)}
          />,
          document.body
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // STATE 3: All done
  // ══════════════════════════════════════════════════════════════════
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.625rem', padding: '0.5rem 0' }}>
      <div style={{ width: '100%' }}>
        <WalkinNotificationCard memberId={memberId} isDark={isDark} />
      </div>
      {/* Doctor availability toggle */}
      <DoctorAvailabilityToggle
        isDark={isDark}
        currentAvail={currentAvail}
        options={AVAILABILITY_OPTIONS}
        onSelect={(key) => memberId && updateDoctorAvailability?.(memberId, key)}
      />
      {/* Rx Practice Tour nudge — shows for doctors who haven't tried it yet */}
      <div style={{ width: '100%' }}>
        <PracticeTourNudge isDark={isDark} />
      </div>
      <span style={{ fontSize: '1.5rem' }}>✅</span>
      <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', fontWeight: 500, color: isDark ? '#4ade80' : '#16a34a' }}>
        All consultations complete
      </p>
      <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
        {completedToday > 0 ? `${completedToday} visits done today` : 'No patients waiting'}
      </p>
      {onNavigate && (
        <button
          onClick={() => onNavigate(null)}
          style={{
            marginTop: '0.5rem',
            cursor: 'pointer',
            padding: '0.375rem 0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            background: 'transparent',
            fontSize: '0.6875rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            transition: 'background 0.15s, opacity 0.15s',
          }}
        >
          View Today's Visits
        </button>
      )}
    </div>
  );
}


// ── DoctorPrescriptionPanel — Add (digital) or Upload prescription ──


// DoctorPrescriptionPanel — Inline prescription sub-view
function DoctorPrescriptionPanel({ visit, isDark, onClose, pendingComplete, onConfirmComplete, role }) {
  // Doctors always get the prescription pad only: no upload tab, and the
  // remembered tab preference is never consulted for them.
  const isDoctor = (role || '').toUpperCase() === STAFF_ROLES.DOCTOR;
  // Use backend-stored preference as default tab ('add' for doctors)
  const { prescriptionTabPreference, prescriptionTabPreferenceFetched, fetchPreferences, recordPrescriptionTabUsage } = useWorkflowStore();
  // Same specialised symptom list the doctor's own accordion (VisitActionsPanel)
  // shows — fall back to the logged-in doctor's own specialization when the
  // visit carries none, instead of silently reading as COMMON-only.
  const ownSpecialization = useOwnDoctorSpecialization();
  const doctorSpecialization = visit.doctor_specialization || ownSpecialization;
  const [subTab, setSubTab] = useState('add');
  const [prescriptions, setPrescriptions] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [editingRx, setEditingRx] = useState(null); // { metaId, data } — prescription being edited

  // Fetch preference once on mount
  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  // Sync subTab once backend preference is actually fetched
  const prefSynced = useRef(false);
  useEffect(() => {
    if (!isDoctor && prescriptionTabPreferenceFetched && !prefSynced.current) {
      setSubTab(prescriptionTabPreference);
      prefSynced.current = true;
    }
  }, [prescriptionTabPreference, prescriptionTabPreferenceFetched, isDoctor]);

  // FIX: Use SyncEngine for deduplicated, cached metadata fetch
  const loadPrescriptions = useCallback(async () => {
    try {
      setLoadingFiles(true);
      const result = await syncEngine.fetchMetadata(
        visit.visit_id,
        async (vid) => fetchVisitMetadata(vid),
        { source: 'DoctorPrescriptionPanel' }
      );
      const data = result?.data;
      const allEntries = data?.entries || [];
      // Attached images live as separate PRESCRIPTION_IMAGE rows, linked
      // back to their parent Rx via prescription_meta_id — group them on
      // here so the auto-load-latest-Rx effect below can carry them
      // forward, same pattern as VisitActionsPanel's prescriptionEntries.
      const imageEntries = allEntries.filter((e) => e.meta_type === META_TYPES.PRESCRIPTION_IMAGE);
      const pEntries = allEntries
        .filter((e) => e.meta_type === META_TYPES.PRESCRIPTION)
        .map((e) => ({
          ...e,
          attached_images: imageEntries
            .filter((img) => img.prescription_meta_id === e.meta_id)
            .sort((a, b) => (a.image_order ?? 0) - (b.image_order ?? 0)),
        }));
      setPrescriptions(pEntries);
    } catch {
      setPrescriptions([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [visit.visit_id]);

  // Wrap onSuccess to also record which tab was used
  const handlePrescriptionSuccess = useCallback((tab) => {
    syncEngine.invalidateMetadata(visit.visit_id);
    loadPrescriptions();
    recordPrescriptionTabUsage(tab);
  }, [visit.visit_id, loadPrescriptions, recordPrescriptionTabUsage]);

  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  // Auto-load existing digital prescription into form on mount, AND
  // re-sync (not just first-load) whenever fresher data for the SAME
  // entry arrives: right after this panel's own onSuccess fires,
  // editingRx.data is set from the SAVE RESPONSE (savedData in
  // DigitalPrescriptionForm), which has no attached_images field at all
  // -- only the child PRESCRIPTION_IMAGE rows carry that -- so
  // editingRx.data.attached_images goes stale/missing immediately
  // post-save even though the backend has already correctly relinked/
  // added images. handlePrescriptionSuccess's loadPrescriptions() re-fetches
  // shortly after; once that lands (same meta_id, fresh attached_images
  // grouped on), this effect re-fires to fill the gap -- found live: a
  // doctor who couldn't see their already-attached images this way
  // re-attached the same photos a second time.
  useEffect(() => {
    // NOTE (merge): the guard is deliberately `loadingFiles` ONLY, not
    // `loadingFiles || editingRx`. The re-sync block below (fresher data for
    // the SAME meta_id) is unreachable if we bail whenever editingRx is set —
    // that early return would silently disable the rx-memory re-sync fix above.
    if (loadingFiles) return;
    // `isDigitalRxEntry` = digital type + the ONE shared content rule
    // (`utils/prescriptionContent`: medicines · investigations ·
    // advised_surgeries · non-blank diagnosis). Both halves matter here:
    //   - CONTENT supersedes the two-term lab-only variant that lived here.
    //     Two terms covered the lab-only Rx (medicines OR investigations) but
    //     still missed diagnosis-only and surgeries-only entries, so the doctor
    //     reopened a visit that already HAS a prescription, got an empty form,
    //     and retyping it saved a SECOND entry (bk-1149, and the input
    //     condition for the bk-1138 merged-print bug).
    //   - TYPE keeps uploads/images out of the pad's re-open set.
    const digital = prescriptions.filter(isDigitalRxEntry);
    if (digital.length > 0) {
      // Most recent digital prescription (by created_at — entry order isn't guaranteed).
      const latest = [...digital].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')).pop();
      // Only act on first load (editingRx unset) or fresher data for the
      // SAME entry — never clobber a save-in-flight targeting something else.
      if (editingRx && editingRx.metaId !== latest.meta_id) return;
      const wasFirstLoad = !editingRx;
      setEditingRx({
        metaId: latest.meta_id,
        data: {
          medicines: latest.medicines || [],
          doctor_notes: latest.doctor_notes || '',
          chief_complaint: latest.chief_complaint || '',
          diagnosis: latest.diagnosis || '',
          report_required: latest.report_required ?? false,
          investigations: latest.investigations || [],
          follow_up_required: latest.follow_up_required ?? false,
          follow_up_in: latest.follow_up_in || null,
          vitals: latest.vitals || [],
          attached_images: latest.attached_images || [],
          allergies: latest.allergies || '',
          advised_surgeries: latest.advised_surgeries || [],
        },
      });
      // Only force the tab on the doctor's very first load into this
      // panel — re-syncing fresher data for an entry they're already
      // editing must not yank them off the 'upload' tab if that's where
      // they are.
      if (wasFirstLoad) setSubTab('add');
    }
  }, [loadingFiles, prescriptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const subTabs = [
    { key: 'add', label: '💊 Add Prescription', icon: '✨' },
    ...(isDoctor ? [] : [{ key: 'upload', label: '📤 Upload Prescription', icon: '📎' }]),
  ];

  return (
    <div style={{
      margin: '0.375rem 0 0', borderRadius: '0.625rem',
      border: `1px solid ${isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.15)'}`,
      background: isDark ? 'rgba(139,92,246,0.04)' : 'rgba(139,92,246,0.02)',
      overflow: 'hidden',
    }}>
      {/* Review banner — shown when completing */}
      {pendingComplete && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '0.5rem', padding: '0.5rem 0.75rem',
          background: isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)',
          borderBottom: `1px solid ${isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`,
        }}>
          <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#86efac' : '#16a34a' }}>
            📋 Review prescription before completing
          </span>
          <button
            onClick={onConfirmComplete}
            style={{
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.375rem 0.75rem', borderRadius: '0.5rem',
              fontSize: '0.6875rem', fontWeight: 600, border: 'none',
              background: isDark ? 'rgba(34,197,94,0.25)' : 'rgba(34,197,94,0.18)',
              color: '#22c55e', whiteSpace: 'nowrap',
            }}
          >
            ✓ Confirm Complete
          </button>
        </div>
      )}

      {/* Header with close */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0.5rem 0.75rem 0',
      }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#c4b5fd' : '#7c3aed' }}>
          💊 Prescription
        </span>
        <button
          onClick={onClose}
          style={{ cursor: 'pointer', border: 'none', background: 'transparent', fontSize: '0.75rem', color: isDark ? '#6b7280' : '#9ca3af' }}
        >
          ✕
        </button>
      </div>

      {/* Sub-tab switcher */}
      <div style={{
        display: 'flex', gap: '0.125rem',
        padding: '0.375rem 0.75rem 0',
        borderBottom: `1px solid ${isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.08)'}`,
      }}>
        {subTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            style={{
              padding: '0.375rem 0.625rem',
              borderRadius: '0.375rem 0.375rem 0 0',
              border: 'none',
              borderBottom: subTab === t.key
                ? `2px solid ${isDark ? '#c4b5fd' : '#7c3aed'}`
                : '2px solid transparent',
              background: subTab === t.key
                ? (isDark ? 'rgba(139,92,246,0.1)' : 'rgba(139,92,246,0.06)')
                : 'transparent',
              fontSize: '0.6875rem',
              fontWeight: subTab === t.key ? 600 : 400,
              color: subTab === t.key
                ? (isDark ? '#c4b5fd' : '#7c3aed')
                : (isDark ? '#9ca3af' : '#6b7280'),
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s, border-color 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div style={{ padding: '0.5rem 0.75rem' }}>
        {subTab === 'add' && (
          <DigitalPrescriptionForm
            visit={visit}
            visitId={visit.visit_id}
            doctorSpecialization={doctorSpecialization}
            onSuccess={(result) => {
              // Keep form in edit-in-place mode with the newly saved meta_id
              if (result?.metaId) {
                setEditingRx({ metaId: result.metaId, data: result.data });
              }
              handlePrescriptionSuccess('add');
            }}
            initialData={editingRx?.data || undefined}
            editMetaId={editingRx?.metaId || undefined}
            patientId={visit.patient_id}
            metadataLoaded={!loadingFiles}
          />
        )}

        {!isDoctor && subTab === 'upload' && (
          <AddPrescription
            visitId={visit.visit_id}
            metaType={META_TYPES.PRESCRIPTION}
            onSuccess={() => handlePrescriptionSuccess('upload')}
          />
        )}


      </div>

    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// NURSE VIEW
// ═══════════════════════════════════════════════════════════════════════
